/**
 * PTEL Visor - MapContainer Component
 * Contenedor de mapa accesible con integración de proveedores de tiles
 *
 * V10 - Medidas de accesibilidad:
 * - A01: role="application" en contenedor
 * - A02: aria-label descriptivo
 * - A03: Navegación por teclado
 * - A04: alt en marcadores (via MarkerIcon)
 * - A06: Supresión de tiles para lectores de pantalla
 *
 * @see src/services/tiles/TileProvider.ts - Gestión de proveedores
 * @see src/services/tiles/TileCache.ts - Caché de tiles
 * @see src/components/map/MarkerIcon.tsx - Marcadores accesibles
 */

import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  memo,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { MapContainer as LeafletMapContainer, TileLayer, useMap } from 'react-leaflet';
import type { Map as LeafletMap, LatLngExpression } from 'leaflet';
import 'leaflet/dist/leaflet.css';

import {
  getTileProvider,
  type ActiveTileConfig,
  type TileProviderId,
} from '../../services/tiles';
// SSCA-FIX-2 (SF2B): guard de montaje para setState tras await
import { useIsMounted } from '@/hooks/useIsMounted';

// =============================================================================
// TIPOS
// =============================================================================

/** Punto geográfico para el mapa */
export interface MapPoint {
  lat: number;
  lng: number;
}

/** Límites del mapa (bounding box) */
export interface MapBounds {
  north: number;
  south: number;
  east: number;
  west: number;
}

/** Estado del mapa */
export type MapStatus = 'loading' | 'ready' | 'error' | 'offline';

/** Props del MapContainer */
export interface MapContainerProps {
  /** Centro inicial del mapa */
  center?: MapPoint;
  /** Zoom inicial (1-20) */
  zoom?: number;
  /** Zoom mínimo permitido */
  minZoom?: number;
  /** Zoom máximo permitido */
  maxZoom?: number;
  /** Altura del contenedor (CSS) */
  height?: string;
  /** Ancho del contenedor (CSS) */
  width?: string;
  /** Clases CSS adicionales */
  className?: string;
  /** ID para accesibilidad */
  id?: string;
  /** Descripción del mapa para lectores de pantalla */
  ariaLabel?: string;
  /** Proveedor de tiles preferido */
  preferredProvider?: TileProviderId;
  /** Callback cuando el mapa está listo */
  onReady?: (map: LeafletMap) => void;
  /** Callback cuando cambia el estado */
  onStatusChange?: (status: MapStatus) => void;
  /** Callback cuando cambia el centro */
  onCenterChange?: (center: MapPoint) => void;
  /** Callback cuando cambia el zoom */
  onZoomChange?: (zoom: number) => void;
  /** Callback en error de tiles */
  onTileError?: (providerId: TileProviderId, error: Error) => void;
  /** Contenido hijo (marcadores, capas, etc.) */
  children?: React.ReactNode;
  /** Deshabilitar interacción */
  disabled?: boolean;
}

/** Métodos expuestos via ref */
export interface MapContainerRef {
  /** Referencia al mapa Leaflet */
  getMap: () => LeafletMap | null;
  /** Centrar mapa en un punto */
  setCenter: (center: MapPoint, zoom?: number) => void;
  /** Ajustar mapa a límites */
  fitBounds: (bounds: MapBounds, padding?: number) => void;
  /** Obtener estado actual */
  getStatus: () => MapStatus;
  /** Forzar recarga de tiles */
  refreshTiles: () => Promise<void>;
}

// =============================================================================
// CONSTANTES
// =============================================================================

/** Centro por defecto: Andalucía (aproximado) */
const DEFAULT_CENTER: MapPoint = { lat: 37.5, lng: -4.5 };

/** Zoom por defecto */
const DEFAULT_ZOOM = 8;

/** Límites de zoom */
const MIN_ZOOM = 5;
const MAX_ZOOM = 19;

/** Teclas de navegación del mapa */
const MAP_NAVIGATION_KEYS = {
  PAN_UP: ['ArrowUp', 'w', 'W'],
  PAN_DOWN: ['ArrowDown', 's', 'S'],
  PAN_LEFT: ['ArrowLeft', 'a', 'A'],
  PAN_RIGHT: ['ArrowRight', 'd', 'D'],
  ZOOM_IN: ['+', '='],
  ZOOM_OUT: ['-', '_'],
  RESET: ['Home', '0'],
};

/** Distancia de pan por pulsación (píxeles) */
const PAN_DISTANCE = 100;

// =============================================================================
// SUBCOMPONENTES
// =============================================================================

/**
 * Componente interno para manejar la capa de tiles
 */
interface TileLayerManagerProps {
  tileConfig: ActiveTileConfig | null;
  onError?: (providerId: TileProviderId, error: Error) => void;
}

const TileLayerManager = memo(function TileLayerManager({
  tileConfig,
  onError,
}: TileLayerManagerProps) {
  if (!tileConfig) {
    return null;
  }

  return (
    <TileLayer
      url={tileConfig.url}
      attribution={tileConfig.attribution}
      minZoom={tileConfig.minZoom}
      maxZoom={tileConfig.maxZoom}
      // Solo pasar subdomains si está definido (IGN no usa subdominios)
      {...(tileConfig.subdomains && { subdomains: tileConfig.subdomains })}
      eventHandlers={{
        tileerror: (event) => {
          if (onError) {
            onError(tileConfig.id, new Error(`Tile error: ${event.error}`));
          }
        },
      }}
    />
  );
});

/**
 * Componente interno para manejar eventos del mapa
 */
interface MapEventHandlerProps {
  onReady?: (map: LeafletMap) => void;
  onCenterChange?: (center: MapPoint) => void;
  onZoomChange?: (zoom: number) => void;
}

function MapEventHandler({
  onReady,
  onCenterChange,
  onZoomChange,
}: MapEventHandlerProps) {
  const map = useMap();

  useEffect(() => {
    if (onReady) {
      onReady(map);
    }
  }, [map, onReady]);

  useEffect(() => {
    const handleMoveEnd = () => {
      if (onCenterChange) {
        const center = map.getCenter();
        onCenterChange({ lat: center.lat, lng: center.lng });
      }
    };

    const handleZoomEnd = () => {
      if (onZoomChange) {
        onZoomChange(map.getZoom());
      }
    };

    map.on('moveend', handleMoveEnd);
    map.on('zoomend', handleZoomEnd);

    return () => {
      map.off('moveend', handleMoveEnd);
      map.off('zoomend', handleZoomEnd);
    };
  }, [map, onCenterChange, onZoomChange]);

  // A06: Suprimir tiles para lectores de pantalla
  // Los tiles de mapa son puramente decorativos y no deben ser anunciados
  useEffect(() => {
    const suppressTilesForScreenReaders = () => {
      const container = map.getContainer();
      container.querySelectorAll('.leaflet-tile-container img').forEach((img) => {
        img.setAttribute('role', 'presentation');
        img.setAttribute('alt', '');
      });
    };

    // Ejecutar inicialmente después de que el mapa esté listo
    suppressTilesForScreenReaders();

    // Ejecutar cada vez que se cargue un nuevo tile
    map.on('tileload', suppressTilesForScreenReaders);

    return () => {
      map.off('tileload', suppressTilesForScreenReaders);
    };
  }, [map]);

  return null;
}

// =============================================================================
// COMPONENTE PRINCIPAL
// =============================================================================

/**
 * Contenedor de mapa PTEL accesible
 *
 * @example
 * ```tsx
 * <MapContainer
 *   center={{ lat: 37.38, lng: -5.99 }}
 *   zoom={12}
 *   ariaLabel="Mapa de infraestructuras de Sevilla"
 *   onReady={(map) => console.log('Mapa listo', map)}
 * >
 *   <MarkerIcon type="HEALTH" />
 * </MapContainer>
 * ```
 */
export const MapContainer = memo(
  forwardRef<MapContainerRef, MapContainerProps>(function MapContainer(
    {
      center = DEFAULT_CENTER,
      zoom = DEFAULT_ZOOM,
      minZoom = MIN_ZOOM,
      maxZoom = MAX_ZOOM,
      height = '100%',
      width = '100%',
      className = '',
      id,
      ariaLabel = 'Mapa de infraestructuras PTEL',
      preferredProvider = 'ign-base',
      onReady,
      onStatusChange,
      onCenterChange,
      onZoomChange,
      onTileError,
      children,
      disabled = false,
    },
    ref
  ) {
    // -------------------------------------------------------------------------
    // Estado
    // -------------------------------------------------------------------------
    const [status, setStatus] = useState<MapStatus>('loading');
    const [tileConfig, setTileConfig] = useState<ActiveTileConfig | null>(null);
    const mapRef = useRef<LeafletMap | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    // SSCA-FIX-2 (SF2B, A=β2): guard de montaje para el setState async de
    // refreshTiles. loadTileConfig conserva su flag local `mounted` — dentro
    // de un useEffect ese flag cubre además la invalidación por cambio de dep.
    const isMounted = useIsMounted();

    // -------------------------------------------------------------------------
    // Efectos
    // -------------------------------------------------------------------------

    // Cargar configuración de tiles
    useEffect(() => {
      let mounted = true;

      async function loadTileConfig() {
        try {
          const provider = getTileProvider({ preferredProvider });
          const config = await provider.getActiveTileConfig();

          if (mounted) {
            setTileConfig(config);
            setStatus('ready');
          }
        } catch (error) {
          console.error('[MapContainer] Error loading tile config:', error);
          if (mounted) {
            setStatus('error');
          }
        }
      }

      loadTileConfig();

      return () => {
        mounted = false;
      };
    }, [preferredProvider]);

    // Notificar cambios de estado
    useEffect(() => {
      if (onStatusChange) {
        onStatusChange(status);
      }
    }, [status, onStatusChange]);

    // -------------------------------------------------------------------------
    // Handlers
    // -------------------------------------------------------------------------

    const handleMapReady = useCallback(
      (map: LeafletMap) => {
        mapRef.current = map;
        if (onReady) {
          onReady(map);
        }
      },
      [onReady]
    );

    // Navegación por teclado (A03)
    const handleKeyDown = useCallback(
      (event: React.KeyboardEvent) => {
        if (disabled || !mapRef.current) return;

        const map = mapRef.current;
        const key = event.key;

        // Pan
        if (MAP_NAVIGATION_KEYS.PAN_UP.includes(key)) {
          event.preventDefault();
          map.panBy([0, -PAN_DISTANCE]);
        } else if (MAP_NAVIGATION_KEYS.PAN_DOWN.includes(key)) {
          event.preventDefault();
          map.panBy([0, PAN_DISTANCE]);
        } else if (MAP_NAVIGATION_KEYS.PAN_LEFT.includes(key)) {
          event.preventDefault();
          map.panBy([-PAN_DISTANCE, 0]);
        } else if (MAP_NAVIGATION_KEYS.PAN_RIGHT.includes(key)) {
          event.preventDefault();
          map.panBy([PAN_DISTANCE, 0]);
        }
        // Zoom
        else if (MAP_NAVIGATION_KEYS.ZOOM_IN.includes(key)) {
          event.preventDefault();
          map.zoomIn();
        } else if (MAP_NAVIGATION_KEYS.ZOOM_OUT.includes(key)) {
          event.preventDefault();
          map.zoomOut();
        }
        // Reset
        else if (MAP_NAVIGATION_KEYS.RESET.includes(key)) {
          event.preventDefault();
          map.setView([center.lat, center.lng], zoom);
        }
      },
      [disabled, center, zoom]
    );

    // -------------------------------------------------------------------------
    // Ref API
    // -------------------------------------------------------------------------

    useImperativeHandle(
      ref,
      () => ({
        getMap: () => mapRef.current,

        setCenter: (newCenter: MapPoint, newZoom?: number) => {
          if (mapRef.current) {
            mapRef.current.setView(
              [newCenter.lat, newCenter.lng],
              newZoom ?? mapRef.current.getZoom()
            );
          }
        },

        fitBounds: (bounds: MapBounds, padding = 0.1) => {
          if (mapRef.current) {
            mapRef.current.fitBounds(
              [
                [bounds.south, bounds.west],
                [bounds.north, bounds.east],
              ],
              { padding: [padding * 100, padding * 100] }
            );
          }
        },

        getStatus: () => status,

        refreshTiles: async () => {
          try {
            const provider = getTileProvider({ preferredProvider });
            const config = await provider.getActiveTileConfig();
            // SSCA-FIX-2 (SF2B): no agendar setState si el componente se
            // desmontó mientras se resolvía la recarga de tiles.
            if (isMounted()) {
              setTileConfig(config);
            }
          } catch (error) {
            console.error('[MapContainer] Error refreshing tiles:', error);
          }
        },
      }),
      [status, preferredProvider, isMounted]
    );

    // -------------------------------------------------------------------------
    // Render
    // -------------------------------------------------------------------------

    const leafletCenter: LatLngExpression = [center.lat, center.lng];

    return (
      <div
        ref={containerRef}
        id={id}
        // A01: role="application" para modo de aplicación
        role="application"
        // A02: aria-label descriptivo
        aria-label={ariaLabel}
        aria-busy={status === 'loading'}
        aria-disabled={disabled}
        tabIndex={disabled ? -1 : 0}
        className={`ptel-map-container ${className}`}
        style={{
          height,
          width,
          position: 'relative',
        }}
        // A03: Navegación por teclado
        onKeyDown={handleKeyDown}
      >
        {/* Instrucciones para lectores de pantalla */}
        <div className="sr-only" aria-live="polite">
          {status === 'loading' && 'Cargando mapa...'}
          {status === 'ready' && `Mapa listo. ${ariaLabel}. Use flechas para navegar, + y - para zoom.`}
          {status === 'error' && 'Error al cargar el mapa. Usando mapa alternativo.'}
        </div>

        {/* Indicador de carga */}
        {status === 'loading' && (
          <div
            className="absolute inset-0 flex items-center justify-center bg-slate-100 z-10"
            aria-hidden="true"
          >
            <div className="text-slate-500">Cargando mapa...</div>
          </div>
        )}

        {/* Mapa Leaflet */}
        {tileConfig && (
          <LeafletMapContainer
            center={leafletCenter}
            zoom={zoom}
            minZoom={minZoom}
            maxZoom={maxZoom}
            style={{ height: '100%', width: '100%' }}
            // Deshabilitar controles por defecto (los manejamos vía teclado)
            zoomControl={false}
            attributionControl={true}
          >
            <TileLayerManager tileConfig={tileConfig} onError={onTileError} />
            <MapEventHandler
              onReady={handleMapReady}
              onCenterChange={onCenterChange}
              onZoomChange={onZoomChange}
            />
            {children}
          </LeafletMapContainer>
        )}

        {/* Overlay de deshabilitado */}
        {disabled && (
          <div
            className="absolute inset-0 bg-slate-900/20 z-20 cursor-not-allowed"
            aria-hidden="true"
          />
        )}
      </div>
    );
  })
);

export default MapContainer;
